// api-origin.ts — where this document's money API is (SC-67335, option C2).
//
// Today the game document is rgs-core-api-gw's /play/dice shell: the API (/v1, /contract-manifest.json) is on the same
// origin. Under C2 the document is the page artifact on Dice's own site (dist/play/index.html, which marks itself with
// <meta name="rgs-page" content="site">), so the player's DPoP key lives in an origin the gateway cannot write, and the API
// is cross-origin.
//
// THE RULE: the API's origin is a fixed function of the page's own origin — https://originals-<game>.<domain> answers to
// https://core-api-gw.<domain>, the shape the gateway's DEPLOY.md uses for both — and never input: not the URL, the
// fragment or storage, so a crafted link cannot point a player's key at another API. A site page on any other host has no
// API it may talk to and refuses to boot. A document that is not the site page (the gateway's shell, the dev server, the
// standalone page) keeps the same origin, as today.

/** The mark the page artifact on Dice's own site carries (scripts/play-page.mjs writes it). */
export const SITE_PAGE_META = "rgs-page";

export type ApiOrigin =
  | { readonly kind: "same-origin" }
  | { readonly kind: "gateway"; readonly origin: string }
  | { readonly kind: "unknown" };

// originals-<game>.<domain>: the game label in lowercase letters, digits and inner hyphens, then a domain of at least two
// labels (a URL's hostname is already lowercase).
const SITE_HOST = /^originals-[a-z0-9]+(?:-[a-z0-9]+)*\.((?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,61}[a-z0-9])$/;

/** Where the API is for a document at `where`; `sitePage` says whether it is the page artifact on the game's site. */
export function apiOriginFor(where: { readonly protocol: string; readonly hostname: string; readonly port: string }, sitePage: boolean): ApiOrigin {
  if (!sitePage) return { kind: "same-origin" };
  const m = where.protocol === "https:" && where.port === "" ? SITE_HOST.exec(where.hostname) : null;
  return m ? { kind: "gateway", origin: `https://core-api-gw.${m[1]}` } : { kind: "unknown" };
}

/** Whether `doc` is the page artifact on the game's own site. */
export function isSitePage(doc: Document): boolean {
  return doc.querySelector(`meta[name="${SITE_PAGE_META}"]`)?.getAttribute("content") === "site";
}

/** This document's API origin. */
export function apiOrigin(): ApiOrigin {
  return apiOriginFor(window.location, isSitePage(document));
}

/** What the API's paths go under: the gateway's origin, or "" on the same origin. */
export function apiBase(o: ApiOrigin): string {
  return o.kind === "gateway" ? o.origin : "";
}
